import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { AgeCategory } from '../../../types/camper';
import './AGMReport.css';

// Statistical data for a single year (the columns carried forward each year).
interface AGMYearStats {
  Adults?: number;
  'Senior Youth'?: number;
  'Youth/Children'?: number;
  Apartment?: string;
  Cottage?: string;
  Dorms?: string;
  Campground?: string;
  covid?: boolean;
}

type YearMap = { [year: string]: AGMYearStats };

// New self-contained report document shape: a year-keyed snapshot of all
// displayed columns plus that year's narrative.
interface AGMReportDoc {
  years?: YearMap;
  registrarReport?: string;
  registrarName?: string;
  reportDate?: string;
}

interface CachedData {
  currentYearStats: AGMYearStats | null;
  lastUpdated: number;
  inventoryData: any;
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Number of historical years shown before the current (live) year.
const HISTORY_YEARS = 6;

// Camp was closed in 2020; this column always displays "Covid".
const COVID_YEAR = 2020;

const NO_DATA = 'No data';

// Pure display helpers (operate on any year map so they can render both the
// live current report and a stored past report).
const metricFrom = (map: YearMap, year: number, field: keyof AGMYearStats): string => {
  if (year === COVID_YEAR) return 'Covid';
  const entry = map[year.toString()];
  if (!entry) return NO_DATA;
  if (entry.covid) return 'Covid';
  const value = entry[field];
  return value === undefined || value === null ? NO_DATA : String(value);
};

const demographicsTotalFrom = (map: YearMap, year: number): string => {
  if (year === COVID_YEAR) return 'Covid';
  const entry = map[year.toString()];
  if (!entry) return NO_DATA;
  if (entry.covid) return 'Covid';
  const adults = entry.Adults;
  const senior = entry['Senior Youth'];
  const youth = entry['Youth/Children'];
  if (adults == null || senior == null || youth == null) return NO_DATA;
  return String(adults + senior + youth);
};

const accommodationTotalFrom = (map: YearMap, year: number): string => {
  if (year === COVID_YEAR) return 'Covid';
  const entry = map[year.toString()];
  if (!entry) return NO_DATA;
  if (entry.covid) return 'Covid';

  const parse = (val?: string): [number, number] | null => {
    if (!val || !val.includes('/')) return null;
    const [occupied, capacity] = val.split('/').map(Number);
    return Number.isNaN(occupied) || Number.isNaN(capacity) ? null : [occupied, capacity];
  };

  const parts = [entry.Apartment, entry.Cottage, entry.Dorms, entry.Campground].map(parse);
  if (parts.some(part => part === null)) return NO_DATA;

  const totalOccupied = parts.reduce((sum, part) => sum + (part as [number, number])[0], 0);
  const totalCapacity = parts.reduce((sum, part) => sum + (part as [number, number])[1], 0);
  return `${totalOccupied}/${totalCapacity}`;
};

const AGMReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yearsData, setYearsData] = useState<YearMap>({});
  const [registrarReport, setRegistrarReport] = useState('');
  const [registrarName, setRegistrarName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [calculatingCurrentYear, setCalculatingCurrentYear] = useState(false);
  // All stored reports (new-shape docs), keyed by year, for the "previous year" menu.
  const [reports, setReports] = useState<{ [year: string]: AGMReportDoc }>({});
  const [showYearMenu, setShowYearMenu] = useState(false);

  const currentYear = new Date().getFullYear();

  // Rolling window: 6 years of history + the current year = 7 columns.
  const reportYears = Array.from(
    { length: HISTORY_YEARS + 1 },
    (_, i) => currentYear - HISTORY_YEARS + i
  );

  const availableReportYears = Object.keys(reports)
    .map(Number)
    .sort((a, b) => b - a);

  const getCachedData = (): CachedData | null => {
    const cached = localStorage.getItem('agmCurrentYearCache');
    if (cached) {
      const data: CachedData = JSON.parse(cached);
      if (Date.now() - data.lastUpdated < CACHE_DURATION) {
        return data;
      }
    }
    return null;
  };

  const saveToCache = (stats: AGMYearStats, inventoryData: any) => {
    const cacheData: CachedData = {
      currentYearStats: stats,
      lastUpdated: Date.now(),
      inventoryData
    };
    localStorage.setItem('agmCurrentYearCache', JSON.stringify(cacheData));
  };

  // Calculate the current year's live statistics from registrations + inventory.
  const calculateCurrentYearStats = useCallback(async (): Promise<AGMYearStats> => {
    const inventoryRef = doc(db, 'inventory', 'accommodations');
    const inventoryDocSnap = await getDoc(inventoryRef);
    const inventoryData = inventoryDocSnap.data();

    const isOccupied = (unit: any) => {
      const status = String(unit?.status || '').toLowerCase();
      return status === 'occupied' || status === 'held';
    };

    let apartmentOccupied = 0;
    let apartmentTotal = 0;
    let cottageOccupied = 0;
    let cottageTotal = 0;
    let dormsOccupied = 0;
    let dormsTotal = 0;
    let campgroundOccupied = 0;
    let campgroundTotal = 0;

    if (inventoryData) {
      if (Array.isArray(inventoryData.Apartment)) {
        apartmentTotal = inventoryData.Apartment.length;
        apartmentOccupied = inventoryData.Apartment.filter(isOccupied).length;
      }

      if (Array.isArray(inventoryData.Cottage)) {
        cottageTotal += inventoryData.Cottage.length;
        cottageOccupied += inventoryData.Cottage.filter(isOccupied).length;
      }
      if (Array.isArray(inventoryData.privateCottage)) {
        cottageTotal += inventoryData.privateCottage.length;
        cottageOccupied += inventoryData.privateCottage.filter(isOccupied).length;
      }

      ['dormA', 'dormB', 'Fireside'].forEach(dormType => {
        if (Array.isArray(inventoryData[dormType])) {
          dormsTotal += inventoryData[dormType].length;
          dormsOccupied += inventoryData[dormType].filter(isOccupied).length;
        }
      });

      ['LowerCourt', 'UpperCourt'].forEach(courtType => {
        if (Array.isArray(inventoryData[courtType])) {
          campgroundTotal += inventoryData[courtType].length;
          campgroundOccupied += inventoryData[courtType].filter(isOccupied).length;
        }
      });
    }

    const registrationsRef = collection(db, 'Registrations');
    const registrationsSnapshot = await getDocs(registrationsRef);

    let adults = 0;
    let seniorYouth = 0;
    let youthChildren = 0;

    registrationsSnapshot.forEach(regDoc => {
      const data = regDoc.data();
      if (data.attendingCampers && Array.isArray(data.attendingCampers)) {
        data.attendingCampers.forEach((camper: any) => {
          const ageCategory = camper.age || camper.category;
          switch (ageCategory) {
            case AgeCategory.Adult:
              adults++;
              break;
            case AgeCategory.AdultStudent:
              seniorYouth++;
              break;
            case AgeCategory.YouthChildren:
              youthChildren++;
              break;
          }
        });
      }
    });

    const stats: AGMYearStats = {
      Adults: adults,
      'Senior Youth': seniorYouth,
      'Youth/Children': youthChildren,
      Apartment: `${apartmentOccupied}/${apartmentTotal}`,
      Cottage: `${cottageOccupied}/${cottageTotal}`,
      Dorms: `${dormsOccupied}/${dormsTotal}`,
      Campground: `${campgroundOccupied}/${campgroundTotal}`
    };

    saveToCache(stats, inventoryData);
    return stats;
  }, []);

  const fetchAGMData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all AGM documents so we can find the most recent saved report
      // (a doc containing a `years` snapshot) and the current year's narrative.
      const agmSnapshot = await getDocs(collection(db, 'AGM'));

      let sourceYears: YearMap = {};
      let mostRecentReportYear = -1;
      let currentYearDoc: AGMReportDoc | null = null;
      const reportsMap: { [year: string]: AGMReportDoc } = {};

      agmSnapshot.forEach(agmDoc => {
        const data = agmDoc.data() as AGMReportDoc;
        const yearNum = Number(agmDoc.id);

        // Only new-shape documents (with a `years` snapshot) are real reports.
        if (data.years) {
          reportsMap[agmDoc.id] = data;
        }

        if (yearNum === currentYear) {
          currentYearDoc = data;
        }

        // Carry history forward from the most recent report before this year.
        if (data.years && yearNum < currentYear && yearNum > mostRecentReportYear) {
          mostRecentReportYear = yearNum;
          sourceYears = data.years;
        }
      });

      setReports(reportsMap);

      // Build the displayed history columns from the most recent report.
      const displayed: YearMap = {};
      reportYears.forEach(year => {
        if (year === currentYear) return;
        const key = year.toString();
        if (sourceYears[key]) {
          displayed[key] = sourceYears[key];
        }
      });

      // Load the current year's narrative if a report was already started/saved.
      if (currentYearDoc) {
        const cy = currentYearDoc as AGMReportDoc;
        if (cy.registrarReport) setRegistrarReport(cy.registrarReport);
        if (cy.registrarName) setRegistrarName(cy.registrarName);
      }

      // Current year column: use cache if fresh, otherwise calculate live.
      const cached = getCachedData();
      const currentStats = cached?.currentYearStats
        ? cached.currentYearStats
        : await calculateCurrentYearStats();

      displayed[currentYear.toString()] = currentStats;
      setYearsData(displayed);
    } catch (err) {
      console.error('Error fetching AGM data:', err);
      setError('Failed to load AGM data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear, calculateCurrentYearStats]);

  const handleRefreshCurrentYear = async () => {
    try {
      setCalculatingCurrentYear(true);
      localStorage.removeItem('agmCurrentYearCache');
      const currentStats = await calculateCurrentYearStats();
      setYearsData(prev => ({
        ...prev,
        [currentYear.toString()]: currentStats
      }));
    } catch (err) {
      console.error('Error calculating current year data:', err);
      setError('Failed to calculate current year data');
    } finally {
      setCalculatingCurrentYear(false);
    }
  };

  // Compose the self-contained snapshot to persist: every displayed column,
  // keyed by year. 2020 is stored as a Covid marker; missing years are omitted.
  const buildSnapshot = (): YearMap => {
    const snapshot: YearMap = {};
    reportYears.forEach(year => {
      const key = year.toString();
      if (year === COVID_YEAR) {
        snapshot[key] = { covid: true };
        return;
      }
      if (yearsData[key]) {
        snapshot[key] = yearsData[key];
      }
    });
    return snapshot;
  };

  const handleSaveReport = async () => {
    try {
      setSaving(true);

      const agmDocRef = doc(db, 'AGM', currentYear.toString());
      const updatedData: AGMReportDoc & { updatedAt: Date } = {
        years: buildSnapshot(),
        registrarReport,
        registrarName,
        reportDate: new Date().toISOString(),
        updatedAt: new Date()
      };

      await setDoc(agmDocRef, updatedData);

      // Reflect the just-saved report in the previous-year menu.
      setReports(prev => ({
        ...prev,
        [currentYear.toString()]: {
          years: updatedData.years,
          registrarReport,
          registrarName,
          reportDate: updatedData.reportDate
        }
      }));

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving AGM report:', err);
      setError('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchAGMData();
  }, [fetchAGMData]);

  // On-screen table helpers (bound to the live current report data).
  const metricValue = (year: number, field: keyof AGMYearStats) => metricFrom(yearsData, year, field);
  const demographicsTotal = (year: number) => demographicsTotalFrom(yearsData, year);
  const accommodationTotal = (year: number) => accommodationTotalFrom(yearsData, year);

  // Build a printable HTML report for any year/data set (current or stored).
  const buildReportHtml = (
    reportYear: number,
    columns: number[],
    dataMap: YearMap,
    narrative: string,
    name: string
  ): string => {
    const cell = (value: string) =>
      `<td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${value}</td>`;
    const yearHeaders = columns
      .map(year => `<th style="border: 1px solid #ddd; padding: 8px; text-align: center;">${year}</th>`)
      .join('');

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <h1 style="text-align: center; color: #2c3e50; margin-bottom: 30px; font-size: 24px;">
          Registrar's Report ${reportYear}
        </h1>

        <div style="margin-bottom: 30px;">
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
              <tr style="background: #f8f9fa;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Group</th>
                ${yearHeaders}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Children/Youth</td>
                ${columns.map(year => cell(metricFrom(dataMap, year, 'Youth/Children'))).join('')}
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Students</td>
                ${columns.map(year => cell(metricFrom(dataMap, year, 'Senior Youth'))).join('')}
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Adults</td>
                ${columns.map(year => cell(metricFrom(dataMap, year, 'Adults'))).join('')}
              </tr>
              <tr style="background: #f8f9fa; font-weight: bold;">
                <td style="border: 1px solid #ddd; padding: 8px;">Totals</td>
                ${columns.map(year => cell(demographicsTotalFrom(dataMap, year))).join('')}
              </tr>
            </tbody>
          </table>
        </div>

        <div style="margin-bottom: 30px;">
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
              <tr style="background: #f8f9fa;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Type</th>
                ${yearHeaders}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Apartment</td>
                ${columns.map(year => cell(metricFrom(dataMap, year, 'Apartment'))).join('')}
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Cottage</td>
                ${columns.map(year => cell(metricFrom(dataMap, year, 'Cottage'))).join('')}
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Dorms</td>
                ${columns.map(year => cell(metricFrom(dataMap, year, 'Dorms'))).join('')}
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Campground</td>
                ${columns.map(year => cell(metricFrom(dataMap, year, 'Campground'))).join('')}
              </tr>
              <tr style="background: #f8f9fa; font-weight: bold;">
                <td style="border: 1px solid #ddd; padding: 8px;">Totals</td>
                ${columns.map(year => cell(accommodationTotalFrom(dataMap, year))).join('')}
              </tr>
            </tbody>
          </table>
        </div>

        <div style="margin-bottom: 30px;">
          <h3 style="color: #2c3e50; margin-bottom: 15px;">Registrar's Report</h3>
          <div style="margin-bottom: 15px; white-space: pre-line;">
            ${narrative || 'No report narrative provided.'}
          </div>
        </div>

        <div style="margin-top: 50px; border-top: 1px solid #ddd; padding-top: 20px;">
          <p style="margin: 0;">Report submitted by: ${name || 'Registrar Name'}</p>
          <p style="margin: 0;">Registrar Berwick United Church Camp Meeting Association ${reportYear}</p>
          <p style="margin: 0;">Data current as of: ${new Date().toLocaleDateString()}</p>
        </div>
      </div>
    `;
  };

  const openPrintWindow = (title: string, bodyHtml: string) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              @media print {
                body { margin: 0; padding: 20px; }
                table { page-break-inside: avoid; }
                .page-break { page-break-before: always; }
              }
            </style>
          </head>
          <body>
            ${bodyHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const handlePrint = () => {
    openPrintWindow(
      `Registrar's Report ${currentYear}`,
      buildReportHtml(currentYear, reportYears, yearsData, registrarReport, registrarName)
    );
  };

  // Print a stored past report exactly as it was saved (read-only, frozen).
  const handlePrintPastYear = (year: number) => {
    setShowYearMenu(false);
    const report = reports[year.toString()];
    if (!report || !report.years) return;
    const columns = Object.keys(report.years).map(Number).sort((a, b) => a - b);
    openPrintWindow(
      `Registrar's Report ${year}`,
      buildReportHtml(
        year,
        columns,
        report.years,
        report.registrarReport || '',
        report.registrarName || ''
      )
    );
  };

  if (loading) {
    return <div className="agm-report loading">Loading AGM report data...</div>;
  }

  if (error) {
    return <div className="agm-report error">{error}</div>;
  }

  return (
    <div className="agm-report">
      <div className="report-header">
        <h2>Registrar's Report {currentYear}</h2>
        <div className="report-actions">
          <button
            onClick={handleSaveReport}
            disabled={saving}
            className="save-button"
          >
            {saving ? 'Saving...' : 'Save Report'}
          </button>
          <button
            onClick={handleRefreshCurrentYear}
            disabled={calculatingCurrentYear}
            className="refresh-button"
          >
            {calculatingCurrentYear ? 'Refreshing...' : 'Refresh Current Year'}
          </button>
          <button onClick={handlePrint} className="print-button">
            Print Report
          </button>
          <div className="previous-year-dropdown">
            <button
              onClick={() => setShowYearMenu(open => !open)}
              className="previous-year-button"
              disabled={availableReportYears.length === 0}
            >
              Previous Year Report
            </button>
            {showYearMenu && availableReportYears.length > 0 && (
              <ul className="previous-year-menu">
                {availableReportYears.map(year => (
                  <li key={year}>
                    <button
                      className="previous-year-option"
                      onClick={() => handlePrintPastYear(year)}
                    >
                      {year}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {saved && <div className="save-success">Report saved successfully!</div>}
        {calculatingCurrentYear && <div className="calculating-notice">Calculating current year data...</div>}
      </div>

      <div className="report-content">
        {/* Demographics Table */}
        <div className="table-section">
          <table className="agm-table">
            <thead>
              <tr>
                <th>Group</th>
                {reportYears.map(year => (
                  <th key={year}>{year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Children/Youth</td>
                {reportYears.map(year => (
                  <td key={year}>{metricValue(year, 'Youth/Children')}</td>
                ))}
              </tr>
              <tr>
                <td>Students</td>
                {reportYears.map(year => (
                  <td key={year}>{metricValue(year, 'Senior Youth')}</td>
                ))}
              </tr>
              <tr>
                <td>Adults</td>
                {reportYears.map(year => (
                  <td key={year}>{metricValue(year, 'Adults')}</td>
                ))}
              </tr>
              <tr className="total-row">
                <td><strong>Totals</strong></td>
                {reportYears.map(year => (
                  <td key={year}><strong>{demographicsTotal(year)}</strong></td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Accommodation Table */}
        <div className="table-section">
          <table className="agm-table">
            <thead>
              <tr>
                <th>Type</th>
                {reportYears.map(year => (
                  <th key={year}>{year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Apartment</td>
                {reportYears.map(year => (
                  <td key={year}>{metricValue(year, 'Apartment')}</td>
                ))}
              </tr>
              <tr>
                <td>Cottage</td>
                {reportYears.map(year => (
                  <td key={year}>{metricValue(year, 'Cottage')}</td>
                ))}
              </tr>
              <tr>
                <td>Dorms</td>
                {reportYears.map(year => (
                  <td key={year}>{metricValue(year, 'Dorms')}</td>
                ))}
              </tr>
              <tr>
                <td>Campground</td>
                {reportYears.map(year => (
                  <td key={year}>{metricValue(year, 'Campground')}</td>
                ))}
              </tr>
              <tr className="total-row">
                <td><strong>Totals</strong></td>
                {reportYears.map(year => (
                  <td key={year}><strong>{accommodationTotal(year)}</strong></td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Registrar's Report Section */}
        <div className="registrar-section">
          <h3>Registrar's Report</h3>
          <div className="registrar-info">
            <input
              type="text"
              placeholder="Registrar Name"
              value={registrarName}
              onChange={(e) => setRegistrarName(e.target.value)}
              className="registrar-name-input"
            />
          </div>
          <textarea
            placeholder="Enter your annual report narrative here..."
            value={registrarReport}
            onChange={(e) => setRegistrarReport(e.target.value)}
            className="registrar-report-textarea"
            rows={10}
          />
        </div>

        <div className="report-footer">
          <p><strong>Report generated on:</strong> {new Date().toLocaleDateString()}</p>
          <p><strong>Data current as of:</strong> {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

export default AGMReport;
